/**
 * Profitability by the technology a project is delivered on.
 *
 * Two things the layout must not let slide:
 *
 * 1. `basis` is printed on the page, not implied. Technology is the PLATFORM
 *    tag on the demand; Offering is the service line and answers a different
 *    question. A board headed only "Technology" invites someone to read these
 *    as service lines and conclude the wrong thing about where to hire.
 * 2. The untagged row stays and is visually distinct. It is not a technology
 *    -- it is the remainder, grouped rather than dropped, so every share on
 *    the page is a fraction of this board's whole rather than of a smaller,
 *    unstated one.
 * 3. This board's REVENUE ties to the company's; its COST does not, by
 *    $867,662.53 and 7.06 margin points. Both the docstring and the untagged
 *    tooltip used to claim the totals matched, which is how a 53.6% margin
 *    walks into a room where the company number is 46.5%. ReconciliationNote
 *    now measures the gap instead. Keeping the untagged row is still right --
 *    it just was never what made the board tie out.
 */
import { Cpu } from "lucide-react";
import { useMemo, useState } from "react";
import { HBars } from "@/components/charts/HBars";
import { PageHeaderBar, PageHeaderMetric } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { CostBasisNote } from "@/components/profitability/CostBasisNote";
import { MarginBadge } from "@/components/profitability/MarginBadge";
import { MarginCell } from "@/components/profitability/MarginCell";
import { ReconciliationNote } from "@/components/profitability/ReconciliationNote";
import { TechnologyDrillInDialog } from "@/components/profitability/TechnologyDrillInDialog";
import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HeroPanel } from "@/components/shared/hero-panel";
import { InlineBar } from "@/components/shared/inline-bar";
import { StatusChip } from "@/components/shared/status-chip";
import { Card } from "@/components/ui/card";
import { useClientSort } from "@/hooks/use-client-sort";
import { useRangeParam } from "@/hooks/use-range-param";
import { useTechnology } from "@/hooks/use-technology";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money, percent } from "@/lib/format";
import type { TechnologyRow } from "@/lib/profitability-api";

const EMPTY_ROWS: TechnologyRow[] = [];

export function TechnologyPage() {
  const [range, setRange] = useRangeParam();
  const { data, isLoading, isError, error, refetch } = useTechnology(range);
  const [focus, setFocus] = useState<string | null>(null);

  const rows = data?.rows ?? EMPTY_ROWS;
  // Sorted in the browser, like the Client Partner board and for the same
  // reason: thirteen rows, all of them already here, and a round trip to
  // reorder thirteen things would be slower as well as more complicated.
  const sorted = useClientSort(
    rows,
    (row, key) => row[key as keyof TechnologyRow] as string | number | null,
    { key: "revenue", dir: "desc" },
  );
  const totals = data?.totals;
  // The untagged row is the remainder, not a platform, so "best" ranks tagged
  // rows only. Otherwise a small untagged group with a high margin would be
  // named the best technology on the board.
  const tagged = useMemo(() => rows.filter((row) => !row.is_untagged), [rows]);
  const untagged = rows.find((row) => row.is_untagged) ?? null;
  const best = useMemo(() => {
    let top: TechnologyRow | null = null;
    for (const row of tagged) {
      if (row.margin_pct === null) continue;
      if (top === null || row.margin_pct > (top.margin_pct ?? Number.NEGATIVE_INFINITY)) top = row;
    }
    return top;
  }, [tagged]);
  const focused = rows.find((row) => row.technology === focus) ?? null;

  const rangeInfo = data?.range;
  // Published before the error branch, so a failed load still shows the title
  // and the range that produced it rather than an unlabelled error.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="Profitability by technology"
        fields={[
          // The board total, in the labelled slot every other page puts its
          // headline figure in. The technology COUNT that used to lead this
          // line is gone: the table below is the count.
          totals
            ? {
                key: "revenue",
                label: "Revenue",
                content: (
                  <PageHeaderMetric
                    value={money(totals.revenue)}
                    badge={<MarginBadge value={totals.margin_pct} benchmark={null} />}
                  />
                ),
              }
            : null,
        ]}
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [totals, rangeInfo, range, setRange],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {totals && best ? (
        <HeroPanel
          eyebrow="Best platform margin"
          figure={percent(best.margin_pct)}
          status={<StatusChip tone="good">{best.technology}</StatusChip>}
          chart={
            tagged.length >= 2 ? (
              <HBars
                label="Margin by platform, largest books first"
                height={Math.min(tagged.length, 6) * 22 + 12}
                rows={tagged.slice(0, 6).map((row) => ({
                  label: row.technology,
                  value: row.margin_pct ?? 0,
                  display: percent(row.margin_pct),
                }))}
              />
            ) : undefined
          }
          facts={[
            { label: "Platforms", value: String(tagged.length) },
            ...(untagged ? [{ label: "Untagged", value: money(untagged.revenue) }] : []),
          ]}
        />
      ) : null}

      <Card flush className="w-full">
        <DataTable
          flush
          dense
          loading={isLoading}
          sort={sorted.sort}
          sortDir={sorted.sortDir}
          onSort={sorted.toggleSort}
          headers={[
            {
              label: "Technology",
              sortKey: "technology",
              // The basis rides on the column's info icon, not as a paragraph
              // over the board: read once it is useful, read every visit it is
              // clutter. The drill-in still prints it in full.
              help: data?.basis
                ? `${COLUMN_HELP.technology} ${data.basis}`
                : COLUMN_HELP.technology,
            },
            {
              label: "Share of revenue",
              help: "Each platform's revenue as a fraction of the whole board. Sort by Revenue; the order is the same.",
            },
            {
              label: "Margin",
              sortKey: "margin_pct",
              className: "text-right",
              help: COLUMN_HELP.margin,
            },
            {
              label: "Revenue",
              sortKey: "revenue",
              className: "text-right",
              help: COLUMN_HELP.revenue,
            },
            { label: "Cost", sortKey: "cost", className: "text-right", help: COLUMN_HELP.cost },
            {
              label: "Profit",
              sortKey: "profit",
              className: "text-right",
              help: COLUMN_HELP.profit,
            },
            { label: "Hours", sortKey: "hours", className: "text-right", help: COLUMN_HELP.hours },
            {
              label: "Projects",
              sortKey: "projects",
              className: "text-right",
              help: COLUMN_HELP.technologyProjects,
            },
          ]}
        >
          {sorted.rows.map((row) => {
            // Display only: a ratio of the board's own revenue. With no board
            // revenue there is no whole to take a share of, so no bar.
            const ratio = totals && totals.revenue > 0 ? row.revenue / totals.revenue : null;
            return (
              <tr
                key={row.technology}
                className={`${ROW_CLASS} ${
                  // The remainder, not a technology -- so it does not read as one.
                  row.is_untagged ? "bg-muted/20 italic text-muted-foreground" : ""
                }`}
              >
                <td className="px-4 py-2.5 font-medium">
                  <button
                    type="button"
                    className="text-left hover:underline"
                    onClick={() => setFocus(row.technology)}
                    title={`See the ${row.technology} book`}
                  >
                    {row.technology}
                  </button>
                  {row.is_untagged ? (
                    <span
                      className="ml-2 text-xs not-italic"
                      title="Demands carrying no platform tag, grouped rather than dropped so every share on this page is a fraction of the whole board."
                    >
                      not a technology
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5">
                  {ratio === null ? (
                    "--"
                  ) : (
                    <InlineBar
                      value={ratio}
                      mode="share"
                      fill={row.is_untagged ? "series2" : "series1"}
                      label={percent(ratio * 100)}
                    />
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <MarginCell value={row.margin_pct} />
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{money(row.revenue)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{money(row.cost)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{money(row.profit)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatHours(row.hours)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{row.projects}</td>
              </tr>
            );
          })}
          {totals && !isLoading ? (
            <tr key="all-technologies" className={TOTAL_ROW_CLASS}>
              <td className="px-4 py-2.5">All technologies</td>
              <td className="px-4 py-2.5" />
              <td className="px-4 py-2.5 text-right">
                <MarginCell value={totals.margin_pct} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.revenue)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.cost)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.profit)}</td>
              <td className="px-4 py-2.5" />
              <td className="px-4 py-2.5" />
            </tr>
          ) : null}
        </DataTable>
      </Card>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="No technology has revenue in this range"
          description="No delivery demand booked revenue in the selected months."
        />
      ) : null}

      <ReconciliationNote reconciliation={data?.reconciliation} />
      <CostBasisNote basis={data?.cost_basis} />

      <TechnologyDrillInDialog
        row={focused}
        range={range}
        basis={data?.basis}
        onOpenChange={(open) => {
          if (!open) setFocus(null);
        }}
      />
    </div>
  );
}
